import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  HelpCircle, ChevronLeft, ChevronRight, Check, Loader2, AlertTriangle,
  Paperclip, RotateCcw, ListChecks, Eye, SkipForward,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import SubmissionBar from "@/components/SubmissionBar";
import { CorrectionsPanel } from "@/components/CorrectionsPanel";
import { YesNoDetailInput } from "@/components/YesNoDetailInput";
import { FieldReviewPanel } from "@/components/FieldReviewPanel";
import { SUBMISSION_STATUS } from "@/lib/strings";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const UNKNOWN = "UNKNOWN";

function SaveIndicator({ state }) {
  const map = {
    idle: null,
    saving: { icon: Loader2, text: "SALVATAGGIO...", cls: "text-sky-700" },
    saved: { icon: Check, text: "SALVATO", cls: "text-green-700" },
    error: { icon: AlertTriangle, text: "ERRORE", cls: "text-red-700" },
  };
  const m = map[state];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${m.cls}`} data-testid={`autosave-${state}`} role="status">
      <Icon className={`w-3.5 h-3.5 ${state === "saving" ? "animate-spin" : ""}`} aria-hidden="true" />
      {m.text}
    </span>
  );
}

function AnswerInput({ question, value, onChange, disabled, testid }) {
  const t = question.question_type;
  if (t === "YES_NO" || t === "YES_NO_UNKNOWN" || t === "CONFIRMATION") {
    return (
      <div className="flex flex-wrap gap-3" role="radiogroup" aria-label={question.question_text}>
        {[{ v: true, l: "SÌ" }, { v: false, l: "NO" }].map((o) => (
          <button key={String(o.v)} type="button" disabled={disabled}
            onClick={() => onChange(o.v)}
            aria-pressed={value === o.v}
            data-testid={`${testid}-${o.v ? "yes" : "no"}`}
            className={`min-w-[110px] min-h-[48px] px-6 py-3 rounded-lg border-2 text-base font-bold transition-colors ${
              value === o.v
                ? "bg-green-700 border-green-700 text-white"
                : "bg-white border-slate-300 text-slate-700 hover:border-green-600"
            }`}>
            {o.l}
          </button>
        ))}
        {t === "YES_NO_UNKNOWN" && (
          <button type="button" disabled={disabled}
            onClick={() => onChange(UNKNOWN)}
            aria-pressed={value === UNKNOWN}
            data-testid={`${testid}-unknown`}
            className={`min-w-[110px] min-h-[48px] px-6 py-3 rounded-lg border-2 text-base font-bold transition-colors ${
              value === UNKNOWN
                ? "bg-amber-500 border-amber-500 text-white"
                : "bg-white border-slate-300 text-slate-700 hover:border-amber-500"
            }`}>
            NON SO
          </button>
        )}
      </div>
    );
  }
  if (t === "SINGLE_SELECT") {
    return (
      <div className="flex flex-col gap-2" role="radiogroup" aria-label={question.question_text}>
        {(question.options || []).map((o) => (
          <button key={o.value} type="button" disabled={disabled}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            data-testid={`${testid}-${o.value}`}
            className={`text-left px-4 py-3 rounded-lg border-2 text-base transition-colors ${
              value === o.value
                ? "bg-green-700 border-green-700 text-white font-semibold"
                : "bg-white border-slate-300 text-slate-700 hover:border-green-600"
            }`}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  if (t === "YES_NO_DETAIL") {
    return <YesNoDetailInput question={question} value={value} onChange={onChange} disabled={disabled} testid={testid} />;
  }
  if (t === "LONG_TEXT") {
    return (
      <Textarea rows={4} value={value ?? ""} disabled={disabled}
        placeholder={question.placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid} className="text-base" />
    );
  }
  const numeric = ["INTEGER", "DECIMAL", "PERCENTAGE", "CURRENCY", "QUANTITY"].includes(t);
  return (
    <div className="flex items-center gap-3">
      <Input
        type={numeric ? "number" : t === "DATE" ? "date" : "text"}
        step="any" value={value ?? ""} disabled={disabled}
        placeholder={question.placeholder}
        onChange={(e) => onChange(numeric ? (e.target.value === "" ? null : e.target.valueAsNumber) : e.target.value)}
        data-testid={testid}
        className="text-base min-h-[48px] max-w-xs" />
      {question.unit && <span className="text-base font-semibold text-slate-600">{question.unit}</span>}
    </div>
  );
}

export default function Questionnaire({ fixedSection } = {}) {
  const { companyId, period, can } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flow, setFlow] = useState(null);
  const [pos, setPos] = useState({ s: 0, q: 0 });
  const [saveState, setSaveState] = useState("idle");
  const [conflict, setConflict] = useState(null);
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState("");
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estimateNote, setEstimateNote] = useState("");
  const [docUploading, setDocUploading] = useState(false);
  const [showTech, setShowTech] = useState(false);
  const [sectionSummary, setSectionSummary] = useState(null);
  const fileRef = useRef(null);
  const saveTimer = useRef(null);
  const [commentDraft, setCommentDraft] = useState("");

  const canWrite = can("esg:write");
  const canSeeTech = can("esg:verify") || user?.is_superadmin;

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/questionnaire`);
      setFlow(data);
    } catch (err) {
      toast.error("Errore caricamento questionario", { description: formatApiError(err) });
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const sections = useMemo(() => flow?.sections || [], [flow]);
  const allSections = useMemo(
    () => sections.map((s) => ({ ...s, vq: s.questions.filter((q) => q.visible) })),
    [sections]
  );
  // FASE 3: /questionario-mef mostra solo la sezione "Dialogo con la banca".
  const visibleQuestions = useMemo(
    () => (fixedSection ? allSections.filter((s) => s.code === fixedSection) : allSections),
    [allSections, fixedSection]
  );
  // FASE 4: posizione nel percorso complessivo calcolata sul questionario intero,
  // anche quando la vista è filtrata sulla sola sezione bancaria.
  const globalPos = useMemo(() => {
    const map = {};
    let i = 0;
    for (const s of allSections) for (const q of s.vq) { i += 1; map[q.question_code] = i; }
    return { map, total: i };
  }, [allSections]);

  // Deep-link (?sezione=…&domanda=…) e avvio dalla prima domanda incompleta
  // quando la vista è dedicata alla sezione MEF (FASE 2).
  const initRef = useRef(false);
  useEffect(() => {
    if (!flow || initRef.current || !visibleQuestions.length) return;
    initRef.current = true;
    const target = searchParams.get("domanda");
    const secCode = fixedSection || searchParams.get("sezione");
    if (!secCode && !target) return;
    const si = visibleQuestions.findIndex((s) => !secCode || s.code === secCode);
    if (si < 0) return;
    const vq = visibleQuestions[si].vq;
    let qi = -1;
    if (target) qi = vq.findIndex((q) => q.question_code === target);
    if (qi < 0 && fixedSection) {
      qi = vq.findIndex((q) => !q.answer || q.answer.answer_value === null
        || q.answer.answer_value === undefined || q.answer.answer_value === "");
    }
    setPos({ s: si, q: Math.max(qi, 0) });
  }, [flow, visibleQuestions, fixedSection, searchParams]);
  const currentSection = visibleQuestions[pos.s];
  const currentQuestion = currentSection?.vq[pos.q];

  const totalVisible = visibleQuestions.reduce((n, s) => n + s.vq.length, 0);
  const currentIndex = visibleQuestions.slice(0, pos.s).reduce((n, s) => n + s.vq.length, 0) + pos.q + 1;

  const ping = useCallback((q, s) => {
    if (!companyId || !period?.id) return;
    api.post(`/companies/${companyId}/periods/${period.id}/questionnaire/session/ping`, {
      current_question_code: q?.question_code, section_code: s?.code,
    }).catch(() => {});
  }, [companyId, period?.id]);

  useEffect(() => {
    if (currentQuestion) ping(currentQuestion, currentSection);
  }, [currentQuestion?.question_code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCommentDraft(currentQuestion?.answer?.comment || "");
  }, [currentQuestion?.question_code]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveComment = useCallback(async (question, text) => {
    try {
      await api.put(
        `/companies/${companyId}/periods/${period.id}/questionnaire/answers/${question.question_code}/comment`,
        { comment: text }
      );
      setFlow((prev) => prev && ({
        ...prev,
        sections: prev.sections.map((s) => ({
          ...s,
          questions: s.questions.map((q) => q.question_code !== question.question_code
            ? q : { ...q, answer: { ...(q.answer || {}), question_code: q.question_code, comment: text, status: q.answer?.status || "COMMENT_ONLY" } }),
        })),
      }));
      toast.success("Commento salvato");
    } catch (err) {
      toast.error("Errore salvataggio commento", { description: formatApiError(err) });
    }
  }, [companyId, period?.id]);

  const saveAnswer = useCallback(async (question, value, force = false, reason = undefined) => {
    setSaveState("saving");
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/questionnaire/answers`,
        { question_code: question.question_code, value, force, ...(reason !== undefined ? { reason } : {}) }
      );
      if (data.conflicts?.length) {
        setConflict({ question, value, conflicts: data.conflicts });
        setSaveState("idle");
        return;
      }
      setSaveState("saved");
      if (data.issues?.length) {
        toast.warning("Controlla il dato inserito", {
          description: data.issues.map((i) => i.message).join(" "),
        });
      }
      await load();
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (err) {
      setSaveState("error");
      toast.error("Errore salvataggio", { description: formatApiError(err) });
    }
  }, [companyId, period?.id, load]);

  const handleChange = (question, value) => {
    updateLocal(question.question_code, value);
    if (["YES_NO", "YES_NO_UNKNOWN", "SINGLE_SELECT", "CONFIRMATION"].includes(question.question_type)) {
      saveAnswer(question, value);
    } else {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => saveAnswer(question, value), 900);
    }
  };

  const updateLocal = (code, value) => {
    setFlow((prev) => {
      if (!prev) return prev;
      const sections = prev.sections.map((s) => ({
        ...s,
        questions: s.questions.map((q) => {
          if (q.question_code !== code) return q;
          const existing = q.answer || {};
          return { ...q, answer: { ...existing, question_code: code, answer_value: value, status: "ANSWERED" } };
        }),
      }));
      return { ...prev, sections };
    });
  };

  const goNext = () => {
    if (!currentSection) return;
    if (pos.q < currentSection.vq.length - 1) {
      setPos({ ...pos, q: pos.q + 1 });
    } else if (pos.s < visibleQuestions.length - 1) {
      setSectionSummary(currentSection);
    } else {
      setSectionSummary(currentSection);
    }
  };

  const goPrev = () => {
    if (pos.q > 0) setPos({ ...pos, q: pos.q - 1 });
    else if (pos.s > 0) setPos({ s: pos.s - 1, q: visibleQuestions[pos.s - 1].vq.length - 1 });
  };

  const continueFromSummary = () => {
    setSectionSummary(null);
    if (pos.s < visibleQuestions.length - 1) setPos({ s: pos.s + 1, q: 0 });
  };

  const uploadDocument = async (question, file) => {
    if (!file) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("question_code", question.question_code);
      const dpCode = (question.datapoint_codes || [])[0];
      if (dpCode) fd.append("datapoint_code", dpCode);
      await api.post(`/companies/${companyId}/periods/${period.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato", { description: file.name });
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    } finally {
      setDocUploading(false);
    }
  };

  const copyPrevious = async (question) => {
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/questionnaire/copy-previous`,
        { question_code: question.question_code });
      toast.success("Dato copiato dall'anno precedente");
      load();
    } catch (err) {
      toast.error("Copia non possibile", { description: formatApiError(err) });
    }
  };

  const setMefScope = async (scope) => {
    try {
      await api.put(`/companies/${companyId}/periods/${period.id}/mef/scope`, { mef_scope: scope });
      toast.success(scope === "PRIORITY_1" ? "Ambito: solo indicatori di Priorità 1" : "Ambito: questionario completo");
      load();
    } catch (err) {
      toast.error("Ambito non aggiornato", { description: formatApiError(err) });
    }
  };

  const submitUnavailable = (question) => {
    if (unavailableReason.trim().length < 10) {
      toast.error("Indica una motivazione di almeno 10 caratteri");
      return;
    }
    updateLocal(question.question_code, UNKNOWN);
    saveAnswer(question, UNKNOWN, false, unavailableReason.trim());
    setUnavailableOpen(false);
    setUnavailableReason("");
  };

  const submitEstimate = async (question, value) => {
    if (estimateNote.trim().length < 10) {
      toast.error("Indica il criterio di stima (almeno 10 caratteri)");
      return;
    }
    if (value === null || value === undefined || value === "" || value === UNKNOWN) {
      toast.error("Inserisci prima il valore stimato nel campo risposta");
      return;
    }
    setSaveState("saving");
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/questionnaire/answers`,
        { question_code: question.question_code, value, is_estimate: true, estimate_note: estimateNote.trim() });
      setSaveState("saved");
      toast.success("Salvato come stima qualitativa");
      setEstimateOpen(false);
      load();
    } catch (err) {
      setSaveState("error");
      toast.error("Stima non salvata", { description: formatApiError(err) });
    }
  };

  if (!flow) {
    return (
      <div className="flex items-center gap-3 text-slate-600 p-8" data-testid="questionnaire-loading">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Caricamento del percorso…
      </div>
    );
  }

  if (sectionSummary) {
    const st = sectionSummary.stats;
    return (
      <div className="max-w-2xl mx-auto mt-8" data-testid="section-summary">
        <Card className="border-slate-200">
          <CardContent className="p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Riepilogo — {sectionSummary.title}</h2>
            <p className="text-sm text-slate-600 mb-6">
              {flow.read_only
                ? "Questa sezione è disponibile in sola lettura."
                : "Hai completato questa sezione del percorso."}
            </p>
            <dl className="grid grid-cols-2 gap-3 text-sm mb-6">
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <dt className="text-green-800 text-xs font-semibold">RISPOSTE COMPLETE</dt>
                <dd className="text-2xl font-extrabold text-green-900" data-testid="summary-answered">{st.answered}</dd>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                <dt className="text-amber-800 text-xs font-semibold">DA VERIFICARE</dt>
                <dd className="text-2xl font-extrabold text-amber-900" data-testid="summary-toverify">{st.to_verify}</dd>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <dt className="text-slate-600 text-xs font-semibold">MANCANTI</dt>
                <dd className="text-2xl font-extrabold text-slate-900" data-testid="summary-missing">{st.missing}</dd>
              </div>
              <div className="rounded-lg bg-sky-50 border border-sky-200 p-4">
                <dt className="text-sky-800 text-xs font-semibold">DOMANDE SEZIONE</dt>
                <dd className="text-2xl font-extrabold text-sky-900">{st.total}</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-3">
              <Button className="bg-green-700 hover:bg-green-800" onClick={continueFromSummary} data-testid="summary-continue">
                {flow.read_only ? "VISUALIZZA SEZIONE" : "CONTINUA"}
                <ChevronRight className="w-4 h-4 ml-1" aria-hidden="true" />
              </Button>
              <Button variant="outline" onClick={() => setSectionSummary(null)} data-testid="summary-review">
                RIVEDI
              </Button>
              <Button variant="ghost" onClick={() => navigate(fixedSection ? "/questionario-mef/riepilogo" : "/questionario/riepilogo")} data-testid="summary-full-review">
                <ListChecks className="w-4 h-4 mr-1" aria-hidden="true" /> Rivedi tutte le risposte
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="max-w-xl mx-auto mt-8 text-center" data-testid="questionnaire-empty">
        <Card className="border-slate-200"><CardContent className="p-8">
          <p className="text-slate-700 font-medium mb-2">Nessuna domanda visibile al momento.</p>
          <p className="text-sm text-slate-500 mb-4">Il percorso potrebbe essere completo o le sezioni potrebbero non essere applicabili con l'opzione di rendicontazione corrente.</p>
          <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-dashboard">Vai alla Dashboard</Button>
        </CardContent></Card>
      </div>
    );
  }

  const progressPct = flow.summary?.valid_completion_percentage ?? flow.progress?.percentage ?? 0;
  const workflowStatus = flow.status_summary?.questionnaire;
  const workflowLabel = SUBMISSION_STATUS[workflowStatus]?.label || workflowStatus;
  const guidedCorrection = ["INTEGRATION_REQUIRED", "READY_FOR_RESUBMISSION"].includes(workflowStatus);
  // Correzione guidata: con il questionario "Da integrare" restano modificabili SOLO
  // le risposte incoerenti o richieste dal consulente (flag `editable` dal backend).
  const isReadOnly = Boolean(flow.read_only) && !(guidedCorrection && currentQuestion.editable);
  const corrections = flow.summary?.corrections || [];
  const blockingCount = flow.summary?.blocking_inconsistencies ?? corrections.length;
  const answer = currentQuestion.answer;
  const answerValue = answer?.answer_value;
  const hasExisting = !answer && currentQuestion.existing_values?.length > 0;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="questionnaire-wizard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-wider text-green-700 uppercase">{currentSection.title}</p>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">{fixedSection === "DIALOGO_BANCARIO" ? "Questionario MEF – Banche" : "Completa il tuo ESG"}</h1>
          <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
            <div data-testid="questionnaire-period-status">Periodo: <strong>{flow.status_summary?.period === "IN_PROGRESS" ? "In corso" : flow.status_summary?.period}</strong></div>
            <div data-testid="questionnaire-workflow-status">Questionario: <strong>{workflowLabel}</strong></div>
            <div data-testid="questionnaire-validation-status">Validazione: <strong><span data-testid="questionnaire-validation-count">{blockingCount}</span> incongruenze bloccanti</strong></div>
            <div data-testid="questionnaire-approval-status">Approvazione: <strong>{flow.status_summary?.approval === "APPROVED" ? "approvato" : "non approvato"}</strong></div>
          </dl>
        </div>
        <div className="flex items-center gap-3">
          <SaveIndicator state={saveState} />
          {fixedSection === "DIALOGO_BANCARIO" && (
            <div className="flex items-center rounded-md border border-slate-300 overflow-hidden text-xs" role="radiogroup" aria-label="Ambito del questionario" data-testid="mef-scope-toggle">
              {[["PRIORITY_1", "Solo Priorità 1"], ["FULL", "Completo"]].map(([v, l]) => (
                <button key={v} type="button" disabled={!canWrite || isReadOnly} aria-pressed={flow.mef_scope === v}
                  onClick={() => setMefScope(v)} data-testid={`mef-scope-${v.toLowerCase()}`}
                  className={`px-3 py-1.5 font-semibold ${flow.mef_scope === v ? "bg-green-700 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {canSeeTech && (
            <Button variant="ghost" size="sm" onClick={() => setShowTech(!showTech)} data-testid="toggle-tech-details">
              <Eye className="w-4 h-4 mr-1" aria-hidden="true" /> Dettagli VSME
            </Button>
          )}
        </div>
      </div>

      <div>
        <div className="flex justify-between text-xs text-slate-600 mb-1.5">
          <span data-testid="questionnaire-valid-percentage">
            {flow.summary?.valid ?? 0} risposte valide su {flow.summary?.applicable ?? 0}
            {" – "}{flow.summary?.inconsistent ?? 0} incoerenti – completamento valido
            {" "}<strong>{progressPct}%</strong>
          </span>
          <span data-testid="question-counter" className="text-right">
            {currentQuestion.official_indicator_number != null ? (
              <span className="leading-snug">
                <span className="block font-semibold text-slate-800" data-testid="mef-official-question-number">
                  Documento MEF – Domanda N. {currentQuestion.official_indicator_number}
                  {currentQuestion.mef_priority && (
                    <span className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${currentQuestion.mef_priority === 1 ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-700"}`} data-testid="mef-priority-badge">
                      Priorità {currentQuestion.mef_priority}
                    </span>
                  )}
                </span>
                <span className="block">
                  {currentSection.title} — domanda {pos.q + 1} di {currentSection.vq.length}
                </span>
                <span className="block">
                  Percorso complessivo — {globalPos.map[currentQuestion.question_code] || currentIndex} di {globalPos.total || totalVisible}
                </span>
              </span>
            ) : (
              <>{currentSection.title} — Domanda {pos.q + 1} di {currentSection.vq.length} (totale {currentIndex}/{totalVisible})</>
            )}
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100} data-testid="questionnaire-progress-bar">
          <div className="h-full bg-green-700 transition-[width] duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <SubmissionBar refreshToken={flow.summary?.blocking_inconsistencies + ":" + (flow.summary?.valid ?? 0) + ":" + (flow.summary?.present ?? 0)} />

      <CorrectionsPanel corrections={corrections} readOnly={Boolean(flow.read_only) && !guidedCorrection} testid="questionnaire-corrections" />

      {isReadOnly && (
        <div
          className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900"
          data-testid="questionnaire-read-only-banner"
        >
          {guidedCorrection
            ? "Questa risposta è valida ed è bloccata. In stato \"Da integrare\" sono modificabili solo le risposte incoerenti o richieste dal consulente."
            : "Questo questionario è stato inviato al consulente ed è in sola lettura. Per modificarlo è necessaria una richiesta di integrazione o riapertura autorizzata."}
        </div>
      )}
      {!isReadOnly && guidedCorrection && flow.read_only && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="questionnaire-correction-editable-banner">
          Risposta da correggere: puoi modificarla. Al salvataggio le validazioni vengono rieseguite subito.
        </div>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6 md:p-8 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900 leading-snug" data-testid="question-text">
              {currentQuestion.question_text}
            </h2>
            {(currentQuestion.tooltip || currentQuestion.help_text) && (
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" aria-label="Spiegazione" data-testid="question-help-trigger"
                    className="shrink-0 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors">
                    <HelpCircle className="w-4.5 h-4.5" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-sm leading-relaxed" data-testid="question-help-content">
                  {currentQuestion.tooltip && <p className="mb-2">{currentQuestion.tooltip}</p>}
                  {currentQuestion.help_text && <p>{currentQuestion.help_text}</p>}
                </PopoverContent>
              </Popover>
            )}
          </div>

          {hasExisting && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-4" data-testid="existing-data-banner">
              <p className="text-sm font-semibold text-sky-900 mb-1">Abbiamo già questo dato</p>
              {currentQuestion.existing_values.map((ev) => (
                <p key={ev.datapoint_code} className="text-sm text-sky-800">
                  <strong>{typeof ev.value === "object" ? JSON.stringify(ev.value) : String(ev.value)}</strong>
                  {ev.unit ? ` ${ev.unit}` : ""} — Fonte: {ev.source_type} · Stato: {ev.status}
                </p>
              ))}
              {!isReadOnly && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="bg-sky-700 hover:bg-sky-800"
                  onClick={() => saveAnswer(currentQuestion, "__CONFIRM__")}
                  data-testid="confirm-existing-button">
                  <Check className="w-4 h-4 mr-1" aria-hidden="true" /> CONFERMA
                </Button>
              </div>
              )}
              <p className="text-xs text-sky-700 mt-2">
                {isReadOnly
                  ? "Il dato è in sola lettura."
                  : "Oppure modifica il valore qui sotto per sostituirlo."}
              </p>
            </div>
          )}

          {currentQuestion.jobtimehr_ready && (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="jobtimehr-hint">
              Questo dato potrà essere sincronizzato automaticamente con JOBTIMEHR quando la connessione sarà attiva.
            </p>
          )}

          <AnswerInput
            question={currentQuestion}
            value={answerValue}
            onChange={(v) => handleChange(currentQuestion, v)}
            disabled={!canWrite || isReadOnly}
            testid={`answer-${currentQuestion.question_code}`}
          />

          {currentQuestion.section_code === "DIALOGO_BANCARIO" && answerValue === UNKNOWN && currentQuestion.answer?.unavailable_reason && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="unavailable-reason-saved">
              Dato non disponibile — motivazione: <strong>{currentQuestion.answer.unavailable_reason}</strong>
            </p>
          )}
          {currentQuestion.section_code === "DIALOGO_BANCARIO" && currentQuestion.answer?.is_estimate && (
            <p className="text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="estimate-saved">
              Stima qualitativa — criterio: <strong>{currentQuestion.answer.estimate_note}</strong>
            </p>
          )}
          {estimateOpen && currentQuestion.section_code === "DIALOGO_BANCARIO" && !isReadOnly && (
            <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 space-y-2" data-testid="estimate-box">
              <label htmlFor="q-estimate" className="text-xs font-semibold text-sky-900">
                Inserisci il valore stimato nel campo risposta e descrivi il criterio di stima (min. 10 caratteri)
              </label>
              <Textarea id="q-estimate" rows={2} value={estimateNote} maxLength={1000}
                onChange={(e) => setEstimateNote(e.target.value)}
                placeholder="Es. stima da bollette dei primi 9 mesi proiettate sull'anno"
                data-testid="estimate-note-input" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEstimateOpen(false)} data-testid="estimate-cancel">Annulla</Button>
                <Button size="sm" className="bg-sky-700 hover:bg-sky-800 text-white"
                  disabled={estimateNote.trim().length < 10}
                  onClick={() => submitEstimate(currentQuestion, answerValue)} data-testid="estimate-save">
                  Salva come stima
                </Button>
              </div>
            </div>
          )}
          {unavailableOpen && currentQuestion.section_code === "DIALOGO_BANCARIO" && !isReadOnly && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="unavailable-box">
              <label htmlFor="q-unavailable" className="text-xs font-semibold text-amber-900">
                Perché il dato non è disponibile? (motivazione obbligatoria, min. 10 caratteri)
              </label>
              <Textarea id="q-unavailable" rows={2} value={unavailableReason} maxLength={1000}
                onChange={(e) => setUnavailableReason(e.target.value)}
                placeholder="Es. il fornitore di energia non ha ancora trasmesso i dati di consumo 2025"
                data-testid="unavailable-reason-input" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setUnavailableOpen(false)} data-testid="unavailable-cancel">Annulla</Button>
                <Button size="sm" className="bg-amber-700 hover:bg-amber-800 text-white"
                  disabled={unavailableReason.trim().length < 10}
                  onClick={() => submitUnavailable(currentQuestion)} data-testid="unavailable-save">
                  Salva "Non disponibile"
                </Button>
              </div>
            </div>
          )}
          {canWrite && !isReadOnly && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2"
              data-testid="question-comment-box">
              <label htmlFor="q-comment" className="text-xs font-medium text-slate-600">
                Commento per il consulente — resta distinto dalla risposta strutturata
              </label>
              <Textarea id="q-comment" rows={2} value={commentDraft} maxLength={2000}
                onChange={(e) => setCommentDraft(e.target.value)}
                placeholder="Es. fonte del dato, dubbi da chiarire con il consulente…"
                data-testid="question-comment-input" />
              <div className="flex justify-end">
                <Button size="sm" variant="outline"
                  disabled={commentDraft === (currentQuestion?.answer?.comment || "")}
                  onClick={() => saveComment(currentQuestion, commentDraft)}
                  data-testid="question-comment-save">
                  Salva commento
                </Button>
              </div>
            </div>
          )}

          {currentQuestion.issues?.length > 0 && (
            <div className="space-y-1" data-testid="question-issues">
              {currentQuestion.issues.map((i) => (
                <p key={i.rule_code} role="alert"
                  className={`flex items-center gap-2 text-sm rounded-md border p-2.5 ${
                    i.severity === "ERROR" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}>
                  <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span>{i.message}</span>
                  {i.severity === "ERROR" && (
                    <span className="ml-auto shrink-0 text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-800 rounded px-1.5 py-0.5"
                      data-testid={`issue-blocking-${i.rule_code}`}>Bloccante</span>
                  )}
                </p>
              ))}
            </div>
          )}

          {answerValue === UNKNOWN && currentQuestion.request_document && !isReadOnly && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="document-request">
              <p className="text-sm text-amber-900 mb-2">
                Nessun problema. Se vuoi, carica un documento utile: <strong>{currentQuestion.request_document}</strong>.
                Lo analizzeremo in una fase successiva.
              </p>
              <input ref={fileRef} type="file" className="hidden" aria-hidden="true"
                onChange={(e) => uploadDocument(currentQuestion, e.target.files?.[0])} />
              <Button size="sm" variant="outline" disabled={docUploading}
                onClick={() => fileRef.current?.click()} data-testid="upload-document-button">
                <Paperclip className="w-4 h-4 mr-1" aria-hidden="true" />
                {docUploading ? "Caricamento…" : "CARICA DOCUMENTO"}
              </Button>
            </div>
          )}

          {currentQuestion.previous_year_answer != null && !answer && !isReadOnly && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4" data-testid="previous-year-banner">
              <p className="text-sm text-indigo-900 mb-2">
                Questo dato proviene dall'anno precedente: <em>{typeof currentQuestion.previous_year_answer === "object" ? JSON.stringify(currentQuestion.previous_year_answer) : String(currentQuestion.previous_year_answer).slice(0, 120)}</em>
              </p>
              <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-800"
                onClick={() => copyPrevious(currentQuestion)} data-testid="copy-previous-button">
                <RotateCcw className="w-4 h-4 mr-1" aria-hidden="true" /> USA I DATI DELL'ANNO PRECEDENTE
              </Button>
            </div>
          )}

          {currentQuestion.evidence_required && (
            <p className={`text-xs rounded-md px-3 py-2 border ${currentQuestion.evidence_count > 0 ? "bg-green-50 border-green-200 text-green-900" : "bg-amber-50 border-amber-300 text-amber-900"}`} data-testid="evidence-required-badge">
              Evidenza documentale obbligatoria (catalogo MEF) · documenti caricati: <strong data-testid="evidence-count">{currentQuestion.evidence_count}</strong>
              {currentQuestion.evidence_count === 0 && " — carica bolletta, DURC o certificazione a supporto"}
            </p>
          )}

          <FieldReviewPanel questionCode={currentQuestion.question_code} refreshToken={saveState} />

          {currentQuestion.why_text && (
            <details className="text-sm text-slate-600" data-testid="why-details">
              <summary className="cursor-pointer font-medium text-green-700 hover:underline">
                Perché me lo chiedi?
              </summary>
              <p className="mt-2 leading-relaxed">{currentQuestion.why_text}</p>
            </details>
          )}

          {showTech && (
            <div className="rounded-md bg-slate-900 text-slate-100 p-3 text-xs font-mono space-y-1" data-testid="tech-details">
              <p>Domanda: {currentQuestion.question_code}</p>
              {(currentQuestion.datapoint_codes || []).map((c) => (
                <p key={c}>→ VSME datapoint: {c}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" onClick={goPrev} disabled={pos.s === 0 && pos.q === 0} data-testid="question-prev">
          <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" /> INDIETRO
        </Button>
        <div className="flex flex-wrap gap-2 justify-end">
          {currentQuestion.allow_unknown && currentQuestion.question_type !== "YES_NO_UNKNOWN" && !isReadOnly
            && currentQuestion.section_code !== "DIALOGO_BANCARIO" && (
            <Button variant="ghost" className="text-amber-700"
              onClick={() => handleChange(currentQuestion, UNKNOWN)} data-testid="question-unknown">
              <SkipForward className="w-4 h-4 mr-1" aria-hidden="true" /> NON SO
            </Button>
          )}
          {currentQuestion.section_code === "DIALOGO_BANCARIO" && !isReadOnly && canWrite && (
            <Button variant="ghost" className="text-amber-700"
              onClick={() => { setUnavailableOpen((o) => !o); setEstimateOpen(false); setUnavailableReason(currentQuestion.answer?.unavailable_reason || ""); }}
              data-testid="question-unavailable">
              <SkipForward className="w-4 h-4 mr-1" aria-hidden="true" /> NON DISPONIBILE
            </Button>
          )}
          {currentQuestion.section_code === "DIALOGO_BANCARIO" && ["NUMBER", "PERCENTAGE", "CURRENCY", "DECIMAL", "INTEGER"].includes(currentQuestion.question_type) && !isReadOnly && canWrite && (
            <Button variant="ghost" className="text-sky-700"
              onClick={() => { setEstimateOpen((o) => !o); setUnavailableOpen(false); setEstimateNote(currentQuestion.answer?.estimate_note || ""); }}
              data-testid="question-estimate">
              STIMA QUALITATIVA
            </Button>
          )}
          <Button className="bg-green-700 hover:bg-green-800" onClick={goNext} data-testid="question-next">
            AVANTI <ChevronRight className="w-4 h-4 ml-1" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {!fixedSection && (
      <div className="flex flex-wrap gap-1.5" data-testid="section-nav">
        {visibleQuestions.map((s, i) => (
          <button key={s.code} type="button"
            onClick={() => setPos({ s: i, q: 0 })}
            data-testid={`section-tab-${s.code.toLowerCase()}`}
            aria-current={i === pos.s ? "true" : undefined}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              i === pos.s ? "bg-green-700 text-white" :
              s.stats.answered === s.stats.total && s.stats.total > 0 ? "bg-green-100 text-green-800 border border-green-300" :
              "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>
            {s.title} {s.stats.answered}/{s.stats.total}
          </button>
        ))}
      </div>
      )}

      <AlertDialog open={!!conflict} onOpenChange={() => setConflict(null)}>
        <AlertDialogContent data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Esiste già un dato approvato</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {conflict?.conflicts.map((c) => (
                  <p key={c.datapoint_code}>
                    <strong>{c.datapoint_code}</strong>: dato attuale <strong>{String(c.current_value)}</strong> ({c.current_status}),
                    nuova risposta <strong>{String(c.new_value)}</strong>.
                  </p>
                ))}
                <p>Vuoi sostituire il dato esistente?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conflict-keep">MANTIENI DATO ATTUALE</AlertDialogCancel>
            <AlertDialogAction className="bg-green-700 hover:bg-green-800" data-testid="conflict-use-new"
              onClick={() => {
                saveAnswer(conflict.question, conflict.value, true);
                setConflict(null);
              }}>
              USA NUOVO DATO
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
